import React from "react";
import get from "lodash/get";
import map from "lodash/map";
import forEach from "lodash/forEach";
import { connect } from "react-redux";
import {
  Badge,
  Button,
  Checkbox,
  Col,
  Form,
  Icon,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Row,
  Select,
} from "antd";
import { withNamespaces } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import classes from "./WizardConfig.css";

import {
  postRandomizationConfig,
  getPermutedBlocksActiveRequest,
} from "../../api/requests";
import { logout, setAppSettings } from "../../reducers/user/actions";
import {
  getRandomizationConfirmStep,
  getDrugAccessPermissionInEventSummary,
  getUnblindingRequestReadPermission
} from "../../reducers/user/selectors";
import { getAdditionalBiasExample } from "../../utils/utilFunctions";
import PermutedBlocksUpload from "./PermutedBlocksUpload";
import PermutedBlocksCSV from "./PermutedBlocksCSV";
import PermutedBlocksJson from "./PermutedBlocksJson";
import PermutedBlocksSimulationForm from "./PermutedBlocksSimulationForm";

const { Option } = Select;

class RandomizationConfigForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      configData: {},
      isLoading: false,
      treatmentGroupsKeys: [],
      treatmentGroupsValues: {},
      followUpGroupsKeys: [],
      followUpGroupsValues: {},
      studyCohortKeys: [],
      studyCohortValues: {},
      permutedBlocksActive: {},
      isPermutedBlocksUpload: false,
    };
  }

  componentDidMount() {
    this.setData();
    this.getPermutedBlocksActive();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.randomizationData.is_configured !==
      this.props.randomizationData.is_configured
    ) {
      this.setData();
    }
  }

  getPermutedBlocksActive = async () => {
    const { data } = await getPermutedBlocksActiveRequest();
    this.setState({
      permutedBlocksActive: data,
    });
  };

  setData = () => {
    const data = this.props.randomizationData;

    const treatmentGroupsKeys =
      data.treatment_groups && data.treatment_groups.length > 0
        ? [...map(data.treatment_groups, () => uuidv4())]
        : [uuidv4()];

    const followUpGroupsKeys =
      data.follow_ups && data.follow_ups.length > 0
        ? [...map(data.follow_ups, () => uuidv4())]
        : [uuidv4()];

    const studyCohortKeys =
      data.study_cohorts && data.study_cohorts.length > 0
        ? [...map(data.study_cohorts, () => uuidv4())]
        : [uuidv4()];

    const treatmentGroupsValues = {};
    const followUpGroupsValues = {};
    const studyCohortValues = {};

    forEach(treatmentGroupsKeys, (key, i) => {
      if (data.treatment_groups && data.treatment_groups[i]) {
        treatmentGroupsValues[key] = {
          ...data.treatment_groups[i],
        };
      }
    });

    forEach(followUpGroupsKeys, (key, i) => {
      if (data.follow_ups && data.follow_ups[i]) {
        followUpGroupsValues[key] = {
          ...data.follow_ups[i],
        };
      }
    });

    forEach(studyCohortKeys, (key, i) => {
      if (data.study_cohorts && data.study_cohorts[i]) {
        studyCohortValues[key] = {
          ...data.study_cohorts[i],
        };
      }
    });

    this.setState({
      configData: data,
      treatmentGroupsKeys,
      treatmentGroupsValues,
      followUpGroupsKeys,
      followUpGroupsValues,
      studyCohortKeys,
      studyCohortValues,
    });
  };

  removeTreatmentGroup = (key) => {
    const treatmentGroupsKeys = [...this.state.treatmentGroupsKeys].filter(
      (k) => key !== k
    );

    this.setState({
      treatmentGroupsKeys,
    });
  };

  addTreatmentGroup = () => {
    this.setState({
      treatmentGroupsKeys: [...this.state.treatmentGroupsKeys, uuidv4()],
    });
  };

  removeFollowUpGroup = (key) => {
    const followUpGroupsKeys = [...this.state.followUpGroupsKeys].filter(
      (k) => key !== k
    );

    this.setState({
      followUpGroupsKeys,
    });
  };

  addFollowUpGroup = () => {
    this.setState({
      followUpGroupsKeys: [...this.state.followUpGroupsKeys, uuidv4()],
    });
  };

  removeStudyCohort = (key) => {
    const studyCohortKeys = [...this.state.studyCohortKeys].filter(
      (k) => key !== k
    );

    this.setState({
      studyCohortKeys,
    });
  };

  addStudyCohort = () => {
    this.setState({
      studyCohortKeys: [...this.state.studyCohortKeys, uuidv4()],
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const data = { ...values };
        delete data["treatment_groups"];
        delete data["followUpGroup"];
        data["treatment_groups"] = [];
        data["follow_ups"] = [];
        data["study_cohorts"] = [];

        Object.keys(values["treatment_groups"]).map((key, i) => {
          data["treatment_groups"].push(values["treatment_groups"][key]);
        });

        Object.keys(values["followUpGroup"]).map((key, i) => {
          data["follow_ups"].push(values["followUpGroup"][key]);
        });

        if (get(values, "study_cohorts")) {
          Object.keys(values["study_cohorts"]).map((key, i) => {
            data["study_cohorts"].push(values["study_cohorts"][key]);
          });
        }

        try {
          await postRandomizationConfig(data);
          message.success(this.props.i18n.t("Saved"));
          this.props.setAppSettings();
          this.props.onSubmit();
        } catch (e) {
          message.error(
            JSON.stringify(get(e, "response.data.errors")) ||
              this.props.i18n.t("Error")
          );

          if (get(e, "response.data.errors")) {
            const errors = {};
            Object.keys(get(e, "response.data.errors")).forEach(
              (errorFieldKey) => {
                if (
                  Array.isArray(get(e, `response.data.errors.${errorFieldKey}`))
                ) {
                  errors[errorFieldKey] = {
                    value: values[errorFieldKey],
                    errors: get(e, `response.data.errors.${errorFieldKey}`).map(
                      (errorMessage) => new Error(errorMessage)
                    ),
                  };
                }
              }
            );
            this.props.form.setFields(errors);
          }
        }
      }
    });
  };

  render() {
    const {
      configData,
      isLoading,
      treatmentGroupsKeys,
      followUpGroupsKeys,
      studyCohortKeys,
      isPermutedBlocksUpload,
      permutedBlocksActive,
    } = this.state;
    const {
      getFieldDecorator,
      getFieldValue,
      validateFields,
    } = this.props.form;
    const { 
      isDisabled,
      drugAccessPermissionInEventSummary
    } = this.props;

    return (
      <div className={classes.formWrapper}>
        <h3 className="headline-default">
          Randomization Config{" "}
          <Badge
            count={
              get(configData, "is_configured")
                ? this.props.i18n.t("NamespaceWizardConfig:Configured")
                : this.props.i18n.t("NamespaceWizardConfig:Needs to be configured")
            }
            style={{
              backgroundColor: get(configData, "is_configured")
                ? "#49c850"
                : "#999",
            }}
          />
          <Badge
            count={
              get(configData, "randomization_status.is_active")
                ? this.props.i18n.t("NamespaceWizardConfig:Ready for randomization")
                : this.props.i18n.t("NamespaceWizardConfig:Randomization not ready, please correct errors.")
            }
            style={{
              backgroundColor: get(configData, "randomization_status.is_active")
                ? "rgba(73,200,80,0.44)"
                : "#6b0101",
            }}
          />
        </h3>

        {get(configData, "randomization_status") ? (
          <div>
            {!get(configData, "randomization_status.is_active") ? (
              <ul>
                {get(configData, "randomization_status.status_messages").map(
                  (message, i) =>
                    message ? (
                      <li
                        style={{
                          color: "#6b0101",
                        }}
                      >
                        <i>{message}</i>
                      </li>
                    ) : null
                )}
              </ul>
            ) : (
              ""
            )}
          </div>
        ) : (
          ""
        )}

        <Form onSubmit={this.handleSubmit}>
          <Row gutter={8}>
            <Col sm={12}>
              <Form.Item
                label={this.props.i18n.t(
                  "NamespaceWizardConfig:Randomization target (limit subjects)"
                )}
              >
                {getFieldDecorator("limit_randomization_subjects", {
                  rules: [
                    {
                      required: true,
                      message: this.props.i18n.t("Required"),
                    },
                  ],
                  initialValue: get(configData, "limit_randomization_subjects"),
                })(<InputNumber disabled={isDisabled} />)}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col sm={24}>
              <div style={{ margin: "18px 0 20px" }}>
                {getFieldDecorator("use_pre_randomization", {
                  initialValue: get(configData, "use_pre_randomization", false),
                  valuePropName: "checked",
                })(
                  <Checkbox disabled={isDisabled}>
                    <span style={{ fontSize: "16px" }}>
                      {this.props.i18n.t("Use pre-randomization")}
                    </span>
                  </Checkbox>
                )}
              </div>
            </Col>
            <Col sm={24}>
              <div style={{ margin: "0 0 20px" }}>
                {getFieldDecorator("validation_required", {
                  initialValue: get(configData, "validation_required", false),
                  valuePropName: "checked",
                })(
                  <Checkbox disabled={isDisabled}>
                    <span style={{ fontSize: "16px" }}>
                      {this.props.i18n.t("NamespaceWizardConfig:Use validate randomization")}
                    </span>
                  </Checkbox>
                )}
              </div>
            </Col>
            <Col sm={24}>
              <div style={{ margin: "0 0 30px" }}>
                {getFieldDecorator("randomization_confirm_step", {
                  initialValue: get(
                    configData,
                    "randomization_confirm_step",
                    false
                  ),
                  valuePropName: "checked",
                })(
                  <Checkbox disabled={isDisabled}>
                    <span style={{ fontSize: "16px" }}>
                      {this.props.i18n.t("NamespaceWizardConfig:Randomization confirm step")}
                    </span>
                  </Checkbox>
                )}
              </div>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col sm={24}>
              <div style={{ margin: "0 0 20px" }}>
                {getFieldDecorator("allow_sms_randomization", {
                  initialValue: get(
                    configData,
                    "allow_sms_randomization",
                    false
                  ),
                  valuePropName: "checked",
                })(
                  <Checkbox disabled={isDisabled}>
                    <span style={{ fontSize: "16px" }}>
                      {this.props.i18n.t("NamespaceWizardConfig:Allow SMS randomization")}
                    </span>
                  </Checkbox>
                )}
              </div>
            </Col>
            <Col sm={24}>
              <div style={{ margin: "0 0 30px" }}>
                {getFieldDecorator("allow_phone_randomization", {
                  initialValue: get(
                    configData,
                    "allow_phone_randomization",
                    false
                  ),
                  valuePropName: "checked",
                })(
                  <Checkbox disabled={isDisabled}>
                    <span style={{ fontSize: "16px" }}>
                      {this.props.i18n.t("NamespaceWizardConfig:Allow phone randomization")}
                    </span>
                  </Checkbox>
                )}
              </div>
            </Col>
          </Row>

          <br />
          <Row gutter={8}>
            <Col sm={12}>
              <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Randomization algorithm")}>
                {getFieldDecorator("randomization_algorithm", {
                  rules: [
                    {
                      required: true,
                      message: this.props.i18n.t("Required"),
                    },
                  ],
                  initialValue: get(configData, "randomization_algorithm"),
                })(
                  <Radio.Group disabled={isDisabled}>
                    <Radio value="true_random">{this.props.i18n.t("True Random")}</Radio>
                    <br />
                    <Radio value="biased_coin">{this.props.i18n.t("Biased Coin")}</Radio>
                    <br />
                    <Radio value="mstb_algorithm">
                      {this.props.i18n.t("Minimum Sufficient Balance (MSB)")}
                    </Radio>
                    <br />
                    <Radio value="permuted_blocks">{this.props.i18n.t("Permuted Blocks")}</Radio>
                  </Radio.Group>
                  /*<Select
                    disabled={isDisabled}
                    placeholder={this.props.i18n.t("Select")}
                  >
                    <Option value="true_random">True Random</Option>
                    <Option value="msb_algorithm">MSB algorithm (Act)</Option>
                    <Option value="mstb_algorithm">
                      MSTB algorithm (Escape)
                    </Option>
                  </Select>*/
                )}
              </Form.Item>
            </Col>
          </Row>

          {/*# only supports permuted blocks for now*/}
          {getFieldValue("randomization_algorithm") === "permuted_blocks" && (
            <Row gutter={8}>
              <Col sm={24}>
                {drugAccessPermissionInEventSummary &&
                    (<div className={classes.controls}>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          this.setState({ isPermutedBlocksUpload: true });
                        }}
                      >
                        {this.props.i18n.t("Upload permuted blocks csv")}
                      </Button>
                      &nbsp;&nbsp;
                      <PermutedBlocksCSV />
                      &nbsp;&nbsp;
                      <PermutedBlocksJson />
                      &nbsp;&nbsp;
                      <PermutedBlocksSimulationForm />
                  </div>)
                }


                <br />
                <div className={classes.result}>
                  <h5 className="headline-default">{this.props.i18n.t("NamespaceWizardConfig:Permuted blocks overview")}</h5>

                  {isLoading && <Icon type="loading" />}

                  {permutedBlocksActive && (
                    <div>
                      <div className={classes.resultItem}>
                        <b>\{this.props.i18n.t("State")}</b>: {get(permutedBlocksActive, "state")}
                      </div>

                      {get(permutedBlocksActive, "state") && (

                        <div>
                            <div className={classes.resultItem}>
                            <b>{this.props.i18n.t("NamespaceWizardConfig:Active subjects in current study")}</b>:{" "}
                            {JSON.stringify(get(permutedBlocksActive, "num_active_subjects"))}
                              </div>
                              <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Has used rows")}</b>:{" "}
                              {JSON.stringify(get(permutedBlocksActive, "has_used_elements"))}
                            </div>

                            <hr/>
                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("User is unblinded")}</b>:{" "}
                              {JSON.stringify(get(permutedBlocksActive, "user_is_unblinded"))}
                            </div>
                            {drugAccessPermissionInEventSummary && get(permutedBlocksActive, "user_is_unblinded") && (
                              <div>
                                <div className={classes.resultItem}>
                                  <b>{this.props.i18n.t("NamespaceWizardConfig:Num used rows")}</b>:{" "}
                                  {get(permutedBlocksActive, "num_used_elements")}
                                </div>
                                <div className={classes.resultItem}>
                                  <b>{this.props.i18n.t("NamespaceWizardConfig:Num rejected rows")}</b>:{" "}
                                  {get(permutedBlocksActive, "num_rejected_elements")}
                                </div>
                                <div className={classes.resultItem}>
                                  <b>{this.props.i18n.t("NamespaceWizardConfig:Num elements remainin")}g</b>:{" "}
                                  {get(permutedBlocksActive, "num_elements_left")}
                                </div>
                                <div className={classes.resultItem}>
                                  <b>{this.props.i18n.t("NamespaceWizardConfig:Cohort stats")}</b>:{" "}
                                  {JSON.stringify(get(permutedBlocksActive, "stats_elements_per_cohort"))}
                                </div>
                                <div className={classes.resultItem}>
                                  <b>{this.props.i18n.t("NamespaceWizardConfig:Site stats")}</b>:{" "}
                                  {JSON.stringify(get(permutedBlocksActive, "stats_elements_per_site"))}
                                </div>
                              </div>
                            )}
                            <hr/>

                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Treatment groups")}</b>:{" "}
                              {JSON.stringify(
                                get(permutedBlocksActive, "treatment_groups")
                              )}
                            </div>
                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Cohorts")}</b>:{" "}
                              {JSON.stringify(
                                get(permutedBlocksActive, "cohorts")
                              )}
                            </div>
                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Site codes")}</b>:{" "}
                              {JSON.stringify(
                                get(permutedBlocksActive, "site_codes")
                              )}
                            </div>
                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Stratification factors")}</b>:{" "}
                              {JSON.stringify(
                                get(permutedBlocksActive, "stratification_factors")
                              )}
                            </div>

                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Block sizes")}</b>:{" "}
                              {JSON.stringify(
                                get(permutedBlocksActive, "block_sizes")
                              )}
                            </div>
                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Number of blocks")}</b>:{" "}
                              {get(permutedBlocksActive, "number_of_blocks")}
                            </div>
                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Number of elements")}</b>:{" "}
                              {get(permutedBlocksActive, "number_of_elements")}
                            </div>
                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Recommended sample size")}</b>:{" "}
                              {get(permutedBlocksActive, "recommended_number_of_subjects")}
                            </div>

                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Strata allocation method")}</b>:{" "}
                              {get(permutedBlocksActive, "strata_allocation_method")}
                            </div>

                            <hr/>

                            {get(permutedBlocksActive, "seed") && (
                              <div className={classes.resultItem}>
                                <b>{this.props.i18n.t("NamespaceWizardConfig:Seed")}</b>:{" "}
                                {JSON.stringify(get(permutedBlocksActive, "seed"))}
                              </div>
                            )}

                            <div className={classes.resultItem}>
                              <b>{this.props.i18n.t("NamespaceWizardConfig:Generation source")}</b>:{" "}
                              {get(permutedBlocksActive, "generation_source")}
                            </div>


                        </div>
                      )}

                    </div>
                  )}
                </div>
              </Col>


              <Col sm={24}>
                <div style={{ margin: "20px 0" }}>
                  {getFieldDecorator("site_factor_in_permuted_blocks", {
                    initialValue: !!get(configData, "site_factor_in_permuted_blocks", false),
                    valuePropName: "checked",
                  })(
                    <Checkbox disabled={isDisabled}>
                      <span style={{ fontSize: "16px" }}>
                        {this.props.i18n.t("NamespaceWizardConfig:Site is a factor in Permuted Blocks algorithm")}
                      </span>
                    </Checkbox>
                  )}
                </div>
              </Col>
              <Col sm={24}>
                <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Voided (deactivated) subject PB row handling")}>
                {getFieldDecorator("voided_subjects_handling", {
                  rules: [
                    {
                      required: true,
                      message: this.props.i18n.t("Required"),
                    },
                  ],
                  initialValue: get(configData, "voided_subjects_handling"),
                })(
                  <Radio.Group disabled={isDisabled}>
                    <Radio value="keep_row_used">{this.props.i18n.t("NamespaceWizardConfig:Keep PB row disabled")}</Radio>
                    <br />
                    <Radio value="reclaim_row">{this.props.i18n.t("NamespaceWizardConfig:Reclaim PB row (use for future subjects)")}</Radio>
                  </Radio.Group>
                  /*<Select
                    disabled={isDisabled}
                    placeholder={this.props.i18n.t("Select")}
                  >
                    <Option value="true_random">True Random</Option>
                    <Option value="msb_algorithm">MSB algorithm (Act)</Option>
                    <Option value="mstb_algorithm">
                      MSTB algorithm (Escape)
                    </Option>
                  </Select>*/
                )}
              </Form.Item>
              </Col>
            </Row>
          )}
          <Col sm={24}>
                <div style={{ margin: "20px 0" }}>
                  {getFieldDecorator("study_has_cohorts", {
                    initialValue: !!get(configData, "study_cohorts.0", false),
                    valuePropName: "checked",
                  })(
                    <Checkbox disabled={isDisabled}>
                      <span style={{ fontSize: "16px" }}>
                        {this.props.i18n.t("NamespaceWizardConfig:Add cohorts to study")}
                      </span>
                    </Checkbox>
                  )}
                </div>
              </Col>
          {getFieldValue("study_has_cohorts") && (
              <Row gutter={8}>
                <Col sm={24}>
                  <div className={classes.treatmentGroup}>
                    <h5 className="headline-default">
                      {this.props.i18n.t("NamespaceWizardConfig:Study cohort config")} &nbsp;&nbsp;
                    </h5>
                    {studyCohortKeys.map((key, i) =>
                      key ? (
                        <div className={classes.itemGroup} key={i}>
                          <div style={{ fontSize: "16px" }}>
                            <b>{this.props.i18n.t("NamespaceWizardConfig:Cohort")} #{i + 1}</b>
                            {studyCohortKeys.length > 1 && (
                              <Icon
                                style={{
                                  margin: "3px 0 5px 10px",
                                  color: "#f00",
                                }}
                                type="delete"
                                onClick={() => {
                                  this.removeStudyCohort(key);
                                }}
                              />
                            )}
                          </div>
                          <Row gutter={8}>
                            <Col lg={8}>
                              <div>
                                <Form.Item label={this.props.i18n.t("Name")}>
                                  {getFieldDecorator(
                                    `study_cohorts[${key}].name`,
                                    {
                                      initialValue: get(
                                        this.state,
                                        `studyCohortValues[${key}].name`,
                                        null
                                      ),
                                      rules: [
                                        {
                                          required: true,
                                          message: this.props.i18n.t("Required"),
                                        },
                                      ],
                                    }
                                  )(
                                    <Input
                                      disabled={isDisabled}
                                      placeholder={this.props.i18n.t("Name")}
                                      allowClear
                                    />
                                  )}
                                </Form.Item>
                              </div>
                            </Col>

                            <Col lg={8}>
                              <div>
                                <Form.Item label={this.props.i18n.t("Type")}>
                                  {getFieldDecorator(
                                    `study_cohorts[${key}].slug`,
                                    {
                                      initialValue: get(
                                        this.state,
                                        `studyCohortValues[${key}].slug`,
                                        null
                                      ),
                                      rules: [
                                        {
                                          required: true,
                                          message: this.props.i18n.t("Required"),
                                        },
                                      ],
                                    }
                                  )(
                                    <Input
                                      disabled={isDisabled}
                                      placeholder={this.props.i18n.t("Type")}
                                      allowClear
                                    />
                                  )}
                                </Form.Item>
                              </div>
                            </Col>

                            <Col lg={8}>
                              <div>
                                <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Subjects Limit (optional)")}>
                                  {getFieldDecorator(
                                    `study_cohorts[${key}].limit_subjects`,
                                    {
                                      initialValue: get(
                                        this.state,
                                        `studyCohortValues[${key}].limit_subjects`,
                                        null
                                      ),
                                      rules: [
                                        {
                                          required: false,
                                          message: this.props.i18n.t("Required"),
                                        },
                                      ],
                                    }
                                  )(
                                    <InputNumber
                                      disabled={isDisabled}
                                      placeholder={this.props.i18n.t("NamespaceWizardConfig:Subjects Limit")}
                                      allowClear
                                    />
                                  )}
                                </Form.Item>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      ) : null
                    )}

                    {studyCohortKeys.filter((item) => item).length < 20 && (
                      <p>
                        <Button
                          disabled={isDisabled}
                          type="button"
                          size="small"
                          style={{ padding: "0.3rem" }}
                          onClick={this.addStudyCohort}
                        >
                          {this.props.i18n.t("NamespaceWizardConfig:Add Study Cohort")}
                        </Button>
                      </p>
                    )}
                  </div>
                </Col>
              </Row>
            )}

          <br />

          <Row gutter={8}>
            <Col sm={24}>
              <div className={classes.treatmentGroup}>
                <h5 className="headline-default">
                  {this.props.i18n.t("NamespaceWizardConfig:Treatment groups config")} &nbsp;&nbsp;
                  <Badge
                    count={
                      get(configData, "has_group_blinded_letters")
                        ? this.props.i18n.t("NamespaceWizardConfig:Letter aliases configured")
                        : this.props.i18n.t("NamespaceWizardConfig:No letter aliases")
                    }
                    style={{
                      backgroundColor: get(
                        configData,
                        "has_group_blinded_letters"
                      )
                        ? "rgba(73,200,80,0.44)"
                        : "#999",
                    }}
                  />{" "}
                  {get(configData, "has_group_blinded_letters") ? (
                    ""
                  ) : (
                    <i
                      style={{
                        fontSize: "12px",
                        fontWeight: "lighter",
                        textTransform: "lowercase",
                      }}
                    >
                      {" "}
                      &nbsp;{this.props.i18n.t("NamespaceWizardConfig:SAVE this config to generate blinded alphabetical aliases for the randomization groups.")}
                    </i>
                  )}
                </h5>
                {treatmentGroupsKeys.map((key, i) =>
                  key ? (
                    <div className={classes.itemGroup} key={i}>
                      <div style={{ fontSize: "16px" }}>
                        <b>Group #{i + 1}</b>
                        {treatmentGroupsKeys.length > 1 && (
                          <Icon
                            style={{
                              margin: "3px 0 5px 10px",
                              color: "#f00",
                            }}
                            type="delete"
                            onClick={() => {
                              this.removeTreatmentGroup(key);
                            }}
                          />
                        )}
                      </div>
                      <Row gutter={8}>
                        <Col lg={6}>
                          <div>
                            <Form.Item label={this.props.i18n.t("Name")}>
                              {getFieldDecorator(
                                `treatment_groups[${key}].name`,
                                {
                                  initialValue: get(
                                    this.state,
                                    `treatmentGroupsValues[${key}].name`,
                                    null
                                  ),
                                  rules: [
                                    {
                                      required: true,
                                      message: this.props.i18n.t("Required"),
                                    },
                                  ],
                                }
                              )(
                                <Input
                                  disabled={isDisabled}
                                  placeholder={this.props.i18n.t("Name")}
                                  allowClear
                                />
                              )}
                            </Form.Item>
                          </div>
                        </Col>

                        <Col lg={6}>
                          <div>
                            <Form.Item label={this.props.i18n.t("Type")}>
                              {getFieldDecorator(
                                `treatment_groups[${key}].type`,
                                {
                                  initialValue: get(
                                    this.state,
                                    `treatmentGroupsValues[${key}].type`,
                                    null
                                  ),
                                  rules: [
                                    {
                                      required: true,
                                      message: this.props.i18n.t("Required"),
                                    },
                                  ],
                                }
                              )(
                                <Input
                                  disabled={isDisabled}
                                  placeholder={this.props.i18n.t("Type")}
                                  allowClear
                                />
                              )}
                            </Form.Item>
                          </div>
                        </Col>
                        {getFieldValue("randomization_algorithm") !==
                          "permuted_blocks" && (
                          <Col lg={6}>
                            <div>
                              <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Base probability (%)")}>
                                {getFieldDecorator(
                                  `treatment_groups[${key}].base_probability`,
                                  {
                                    initialValue: get(
                                      this.state,
                                      `treatmentGroupsValues[${key}].base_probability`,
                                      null
                                    ),
                                    rules: [
                                      {
                                        required: true,
                                        message: this.props.i18n.t("Required"),
                                      },
                                    ],
                                  }
                                )(
                                  <InputNumber
                                    disabled={isDisabled}
                                    placeholder={this.props.i18n.t("NamespaceWizardConfig:Base probability")}
                                    allowClear
                                  />
                                )}
                              </Form.Item>
                            </div>
                          </Col>
                        )}
                        {getFieldValue("randomization_algorithm") ===
                          "permuted_blocks" &&
                          getFieldValue("study_has_cohorts") && (
                            <Col lg={6}>
                              <div>
                                <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Cohort slug")}>
                                  {getFieldDecorator(
                                    `treatment_groups[${key}].cohort`,
                                    {
                                      initialValue: get(
                                        this.state,
                                        `treatmentGroupsValues[${key}].cohort`,
                                        null
                                      ),
                                      rules: [
                                        {
                                          required: false,
                                          message: this.props.i18n.t("NamespaceWizardConfig:Required"),
                                        },
                                      ],
                                    }
                                  )(
                                    <Input
                                      disabled={isDisabled}
                                      placeholder={this.props.i18n.t("NamespaceWizardConfig:Cohort slug")}
                                      allowClear
                                    />
                                  )}
                                </Form.Item>
                              </div>
                            </Col>
                          )}
                      </Row>
                    </div>
                  ) : null
                )}

                {treatmentGroupsKeys.filter((item) => item).length < 20 && (
                  <p>
                    <Button
                      disabled={isDisabled}
                      type="button"
                      size="small"
                      style={{ padding: "0.3rem" }}
                      onClick={this.addTreatmentGroup}
                    >
                      {this.props.i18n.t("NamespaceWizardConfig:Add Treatment Group")}
                    </Button>
                  </p>
                )}
              </div>
            </Col>
          </Row>

          <br />

          <Row gutter={8}>
            <Col sm={24}>
              <div className={classes.treatmentGroup}>
                <h5 className="headline-default">{this.props.i18n.t("NamespaceWizardConfig:Follow Ups config")}</h5>
                {followUpGroupsKeys.map((key, i) =>
                  key ? (
                    <div className={classes.itemGroup} key={i}>
                      <div style={{ fontSize: "16px" }}>
                        <b>{this.props.i18n.t("NamespaceWizardConfig:Visit")} #{i + 1}</b>
                        {followUpGroupsKeys.length > 1 && (
                          <Icon
                            style={{
                              margin: "3px 0 5px 10px",
                              color: "#f00",
                            }}
                            type="delete"
                            onClick={() => {
                              this.removeFollowUpGroup(key);
                            }}
                          />
                        )}
                      </div>
                      <Row gutter={8}>
                        <Col lg={9}>
                          <div>
                            <Form.Item label={this.props.i18n.t("Name*")}>
                              {getFieldDecorator(`followUpGroup[${key}].name`, {
                                initialValue: get(
                                  this.state,
                                  `followUpGroupsValues[${key}].name`,
                                  null
                                ),
                                rules: [
                                  {
                                    required: true,
                                    message: this.props.i18n.t("Required"),
                                  },
                                ],
                              })(
                                <Input
                                  disabled={isDisabled}
                                  placeholder={this.props.i18n.t("Name")}
                                  allowClear
                                />
                              )}
                            </Form.Item>
                          </div>
                        </Col>
                        <Col lg={2}>
                          <div>
                            <Form.Item label={this.props.i18n.t("Value")}>
                              {getFieldDecorator(
                                `followUpGroup[${key}].value`,
                                {
                                  initialValue: get(
                                    this.state,
                                    `followUpGroupsValues[${key}].value`,
                                    null
                                  ),
                                  rules: [
                                    {
                                      required: true,
                                      message: this.props.i18n.t("Required"),
                                    },
                                  ],
                                }
                              )(
                                <InputNumber
                                  disabled={isDisabled}
                                  placeholder={this.props.i18n.t("Value")}
                                  allowClear
                                  onChange={() => {
                                    setTimeout(() => {
                                      validateFields([
                                        `followUpGroup[${key}].follow_up_before_warn`,
                                        `followUpGroup[${key}].follow_up_until_overdue`,
                                      ]);
                                    }, 0);
                                  }}
                                />
                              )}
                            </Form.Item>
                          </div>
                        </Col>

                        <Col lg={2}>
                          <div>
                            <Form.Item label={this.props.i18n.t("Type")}>
                              {getFieldDecorator(`followUpGroup[${key}].type`, {
                                initialValue: get(
                                  this.state,
                                  `followUpGroupsValues[${key}].type`,
                                  null
                                ),
                                rules: [
                                  {
                                    required: true,
                                    message: this.props.i18n.t("Required"),
                                  },
                                ],
                              })(
                                <Select
                                  disabled={isDisabled}
                                  placeholder={this.props.i18n.t("Select")}
                                  onChange={() => {
                                    setTimeout(() => {
                                      validateFields([
                                        `followUpGroup[${key}].follow_up_before_warn`,
                                        `followUpGroup[${key}].follow_up_until_overdue`,
                                      ]);
                                    }, 0);
                                  }}
                                >
                                  <Option value="hours">{this.props.i18n.t("Hours")}</Option>
                                  <Option value="days">{this.props.i18n.t("Days")}</Option>
                                </Select>
                              )}
                            </Form.Item>
                          </div>
                        </Col>
                        <Col lg={9}>
                          <div>
                            <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Slug (follow_up_[name])")}>
                              {getFieldDecorator(`followUpGroup[${key}].slug`, {
                                initialValue: get(
                                  this.state,
                                  `followUpGroupsValues[${key}].slug`,
                                  null
                                ),
                                rules: [
                                  {
                                    required: false,
                                    message: this.props.i18n.t("Required"),
                                  },
                                ],
                              })(
                                <Input
                                  disabled={isDisabled}
                                  placeholder={this.props.i18n.t("Slug")}
                                  allowClear
                                />
                              )}
                            </Form.Item>
                          </div>
                        </Col>
                        <Col lg={3}>
                          <div>
                            <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Warn before")}>
                              {getFieldDecorator(
                                `followUpGroup[${key}].follow_up_before_warn`,
                                {
                                  initialValue: get(
                                    this.state,
                                    `followUpGroupsValues[${key}].follow_up_before_warn`,
                                    null
                                  ),
                                  rules: [
                                    {
                                      required: false,
                                      message: this.props.i18n.t("Required"),
                                    },
                                    {
                                      validator: (rule, value, callback) => {
                                        const fwValue = getFieldValue(
                                          `followUpGroup[${key}].value`
                                        );
                                        const fwType = getFieldValue(
                                          `followUpGroup[${key}].type`
                                        );
                                        const bwType = getFieldValue(
                                          `followUpGroup[${key}].follow_up_before_warn_type`
                                        );

                                        if (
                                          fwValue &&
                                          fwType &&
                                          bwType &&
                                          value &&
                                          Number.isInteger(+value)
                                        ) {
                                          const fwHours =
                                            fwType === "days"
                                              ? fwValue * 24
                                              : fwValue;
                                          const bwHours =
                                            bwType === "days"
                                              ? value * 24
                                              : value;

                                          try {
                                            if (+bwHours > fwHours) {
                                              throw new Error();
                                            }
                                            callback();
                                          } catch (err) {
                                            callback(err);
                                          }
                                        } else {
                                          callback();
                                        }
                                      },
                                      message: this.props.i18n.t(`NamespaceWizardConfig:Value should be less than follow up scheduled`),
                                    },
                                  ],
                                }
                              )(
                                <InputNumber
                                  disabled={isDisabled}
                                  placeholder={getFieldValue(
                                    `followUpGroup[${key}].follow_up_before_warn_type`
                                  )}
                                  allowClear
                                />
                              )}
                            </Form.Item>
                          </div>
                        </Col>

                        <Col lg={3}>
                          <div>
                            <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Before type")}>
                              {getFieldDecorator(
                                `followUpGroup[${key}].follow_up_before_warn_type`,
                                {
                                  initialValue: get(
                                    this.state,
                                    `followUpGroupsValues[${key}].follow_up_before_warn_type`,
                                    null
                                  ),
                                  rules: [
                                    {
                                      required: false,
                                      message: this.props.i18n.t("Required"),
                                    },
                                  ],
                                }
                              )(
                                <Select
                                  disabled={isDisabled}
                                  placeholder={this.props.i18n.t("Select")}
                                  onChange={() => {
                                    setTimeout(() => {
                                      validateFields([
                                        `followUpGroup[${key}].follow_up_before_warn`,
                                      ]);
                                    }, 0);
                                  }}
                                >
                                  <Option value="hours">{this.props.i18n.t("Hours")}</Option>
                                  <Option value="days">{this.props.i18n.t("Days")}</Option>
                                </Select>
                              )}
                            </Form.Item>
                          </div>
                        </Col>
                        <Col lg={3}>
                          <div>
                            <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Until overdue")}>
                              {getFieldDecorator(
                                `followUpGroup[${key}].follow_up_until_overdue`,
                                {
                                  initialValue: get(
                                    this.state,
                                    `followUpGroupsValues[${key}].follow_up_until_overdue`,
                                    null
                                  ),
                                  rules: [
                                    {
                                      required: false,
                                      message: this.props.i18n.t("Required"),
                                    },
                                  ],
                                }
                              )(
                                <InputNumber
                                  disabled={isDisabled}
                                  placeholder={getFieldValue(
                                    `followUpGroup[${key}].follow_up_until_overdue_type`
                                  )}
                                  allowClear
                                />
                              )}
                            </Form.Item>
                          </div>
                        </Col>

                        <Col lg={3}>
                          <div>
                            <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Overdue type")}>
                              {getFieldDecorator(
                                `followUpGroup[${key}].follow_up_until_overdue_type`,
                                {
                                  initialValue: get(
                                    this.state,
                                    `followUpGroupsValues[${key}].follow_up_until_overdue_type`,
                                    null
                                  ),
                                  rules: [
                                    {
                                      required: false,
                                      message: this.props.i18n.t("Required"),
                                    },
                                  ],
                                }
                              )(
                                <Select
                                  disabled={isDisabled}
                                  placeholder={this.props.i18n.t("Select")}
                                  onChange={() => {
                                    setTimeout(() => {
                                      validateFields([
                                        `followUpGroup[${key}].follow_up_until_overdue`,
                                      ]);
                                    }, 0);
                                  }}
                                >
                                  <Option value="hours">{this.props.i18n.t("Hours")}</Option>
                                  <Option value="days">{this.props.i18n.t("Days")}</Option>
                                </Select>
                              )}
                            </Form.Item>
                          </div>
                        </Col>
                        <Col lg={4}>
                          <div>
                            <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:On dashboard")}>
                              {getFieldDecorator(
                                `followUpGroup[${key}].track_on_dashboard`,
                                {
                                  initialValue: get(
                                    this.state,
                                    `followUpGroupsValues[${key}].track_on_dashboard`,
                                    null
                                  ),
                                  valuePropName: "checked",
                                }
                              )(
                                <Checkbox disabled={isDisabled}>
                                  <span style={{ fontSize: "16px" }}></span>
                                </Checkbox>
                              )}
                            </Form.Item>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ) : null
                )}

                {followUpGroupsKeys.filter((item) => item).length < 20 && (
                  <p>
                    <Button
                      disabled={isDisabled}
                      type="button"
                      size="small"
                      style={{ padding: "0.3rem" }}
                      onClick={this.addFollowUpGroup}
                    >
                      {this.props.i18n.t("NamespaceWizardConfig:Add Follow Up Group")}
                    </Button>
                  </p>
                )}
              </div>
            </Col>
          </Row>

          <br />

          {getFieldValue("randomization_algorithm") === "mstb_algorithm" && (
            <Row gutter={8}>
              <Col sm={6}>
                <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:MSTB algorithm settings")}>
                  {getFieldDecorator("MSTB_algorithm_settings.mixed_factors", {
                    initialValue: get(
                      configData,
                      "MSTB_algorithm_settings.mixed_factors"
                    ),
                  })(
                    <Select
                      disabled={isDisabled}
                      placeholder={this.props.i18n.t("Select")}
                      mode="multiple"
                    >
                      <Option value="group_balance">{this.props.i18n.t("NamespaceWizardConfig:Group balance")}</Option>
                      <Option value="group_by_site_balance">
                        {this.props.i18n.t("NamespaceWizardConfig:Group by site balance")}
                      </Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={8}>
            <Col sm={12}>
              <Form.Item
                label={this.props.i18n.t(
                  "NamespaceWizardConfig:Start algorithm after number of subjects"
                )}
              >
                {getFieldDecorator("algorithm_start_after_subject_count", {
                  initialValue: get(
                    configData,
                    "algorithm_start_after_subject_count"
                  ),
                })(<InputNumber disabled={isDisabled} />)}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col sm={6}>
              <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Additional bias")}>
                {getFieldDecorator("additional_bias", {
                  rules: [
                    {
                      required: true,
                      message: this.props.i18n.t("NamespaceWizardConfig:Required"),
                    },
                  ],
                  initialValue: get(configData, "additional_bias"),
                })(<InputNumber disabled={isDisabled} />)}
                {!!getFieldValue("additional_bias") && (
                  <span>
                    <i>
                      {this.props.i18n.t("Ex:")}{" "}
                      {getAdditionalBiasExample(
                        getFieldValue("additional_bias")
                      )}
                    </i>
                  </span>
                )}
              </Form.Item>
            </Col>
            <Col sm={6}>
              <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:PK Limit")}>
                {getFieldDecorator("pk_limit", {
                  rules: [
                    {
                      required: true,
                      message: this.props.i18n.t("Required"),
                    },
                  ],
                  initialValue: get(configData, "pk_limit"),
                })(<InputNumber disabled={isDisabled} />)}
              </Form.Item>
            </Col>
          </Row>

          {getFieldValue("randomization_algorithm") === "mstb_algorithm" && (
            <Row gutter={8}>
              <Col sm={12}>
                <Form.Item
                  label={this.props.i18n.t(
                    "NamespaceWizardConfig:Increase algorith bias after number of subjects (step 2)"
                  )}
                >
                  {getFieldDecorator(
                    "algorithm_aggressive_after_subject_count",
                    {
                      initialValue: get(
                        configData,
                        "algorithm_aggressive_after_subject_count"
                      ),
                    }
                  )(<InputNumber disabled={isDisabled} />)}
                </Form.Item>
              </Col>
            </Row>
          )}

          {getFieldValue("randomization_algorithm") === "mstb_algorithm" &&
            !!getFieldValue("algorithm_aggressive_after_subject_count") && (
              <Row gutter={8}>
                <Col sm={6}>
                  <Form.Item
                    label={this.props.i18n.t("NamespaceWizardConfig:Step 2 additional bias")}
                  >
                    {getFieldDecorator("algorithm_aggressive_additional_bias", {
                      rules: [
                        {
                          required: false,
                          message: this.props.i18n.t("Required"),
                        },
                      ],
                      initialValue: get(
                        configData,
                        "algorithm_aggressive_additional_bias"
                      ),
                    })(<InputNumber disabled={isDisabled} />)}
                    {!!getFieldValue(
                      "algorithm_aggressive_additional_bias"
                    ) && (
                      <span>
                        <i>
                          Ex:{" "}
                          {getAdditionalBiasExample(
                            getFieldValue(
                              "algorithm_aggressive_additional_bias"
                            )
                          )}
                        </i>
                      </span>
                    )}
                  </Form.Item>
                </Col>
                <Col sm={6}>
                  <Form.Item label={this.props.i18n.t("Step 2 PK Limit")}>
                    {getFieldDecorator("algorithm_aggressive_pk_limit", {
                      rules: [
                        {
                          required: false,
                          message: this.props.i18n.t("Required"),
                        },
                      ],
                      initialValue: get(
                        configData,
                        "algorithm_aggressive_pk_limit"
                      ),
                    })(<InputNumber disabled={isDisabled} />)}
                  </Form.Item>
                </Col>
              </Row>
            )}

          {getFieldValue("randomization_algorithm") === "mstb_algorithm" &&
            !!getFieldValue("algorithm_aggressive_after_subject_count") && (
              <Row gutter={8}>
                <Col sm={12}>
                  <Form.Item
                    label={this.props.i18n.t(
                      "NamespaceWizardConfig:Limit factors when increasing bias on (step 2) field_name CSV"
                    )}
                  >
                    {getFieldDecorator(
                      "algorithm_aggressive_limit_factors_csv",
                      {
                        initialValue: get(
                          configData,
                          "algorithm_aggressive_limit_factors_csv"
                        ),
                      }
                    )(<Input disabled={isDisabled} />)}
                  </Form.Item>
                </Col>
              </Row>
            )}

          <Row gutter={8}>
            <Col sm={24}>
              <Form.Item label={this.props.i18n.t("NamespaceWizardConfig:Conflicting new subjects handling method (API)")}>
              {getFieldDecorator("conflicting_subjects_handling_method", {
                rules: [
                  {
                    required: true,
                    message: this.props.i18n.t("Required"),
                  },
                ],
                initialValue: get(configData, "conflicting_subjects_handling_method"),
              })(
                <Radio.Group disabled={isDisabled}>
                  <Radio value="raise_error">{this.props.i18n.t("NamespaceWizardConfig:Raise an error")}</Radio>
                  <br />
                  <Radio value="return_data">{this.props.i18n.t("NamespaceWizardConfig:Return existing subject data via api")}</Radio>
                </Radio.Group>
              )}
            </Form.Item>
            </Col>
          </Row>

          <Row gutter={8}>
            <Col>
              <Button
                disabled={isDisabled}
                type="primary"
                size="large"
                htmlType="submit"
              >
                {this.props.i18n.t("Save")}
                {isLoading && <Icon type="loading" />}
              </Button>
            </Col>
          </Row>
        </Form>

        <Modal
          visible={isPermutedBlocksUpload}
          onCancel={() => {
            this.setState({ isPermutedBlocksUpload: false });
          }}
          footer={null}
        >
          {isPermutedBlocksUpload && (
            <PermutedBlocksUpload
              onSubmit={() => {
                this.getPermutedBlocksActive();
                this.setState({ isPermutedBlocksUpload: false });
              }}
              onCancel={() => {
                this.setState({ isPermutedBlocksUpload: false });
              }}
            />
          )}
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  randomizationConfirmStep: getRandomizationConfirmStep(state),
  drugAccessPermissionInEventSummary: getDrugAccessPermissionInEventSummary(state),
});
const mapDispatchToProps = { setAppSettings, logout };

export default Form.create({ name: "randomizationConfig" })(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(withNamespaces()(RandomizationConfigForm))
);



// WEBPACK FOOTER //
// ./src/containers/WizardConfig/RandomizationConfigForm.js